import { jsx, jsxs } from 'react/jsx-runtime';
import { useEffect, useMemo } from 'react';
import * as Headlessui from '@headlessui/react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon.es.js';
import { FixedBannerProvider } from './FixedBanner.Context/FixedBanner.Context.es.js';
import { FixedBannerDescription } from './FixedBanner.Description/FixedBanner.Description.es.js';
import { FixedBannerFooter } from './FixedBanner.Footer/FixedBanner.Footer.es.js';
import { FixedBannerTitle } from './FixedBanner.Title/FixedBanner.Title.es.js';

const FixedBanner = ({ status = 'info', level = 'page', multiline, animationVariant = 'default', className, onDismiss, autoDismiss, children, visible = true, hideIcon = false, locale = {
    dismiss: 'Dismiss'
}, ...rest })=>{
    const fixedBannerClass = classNames(// Base FixedBanner Style
    'cb-fixed-banner', // FixedBanner level style
    `level-${level}`, `status-${status}`, {
        multiline: multiline
    }, className);
    useEffect(()=>{
        let timeout;
        if (autoDismiss && onDismiss) {
            // arbitrary timeout of 5s for dismiss. Option not given to ensure consistency.
            timeout = setTimeout(onDismiss, 5000);
        }
        return ()=>{
            if (timeout != null) {
                clearTimeout(timeout);
            }
        };
    }, [
        autoDismiss,
        onDismiss
    ]);
    const fixedBannerIcon = useMemo(()=>{
        switch(status){
            case 'info':
                return 'InformationCircleSolid';
            case 'success':
                return 'CheckCircleSolid';
            case 'warning':
                return 'ExclamationSolid';
            case 'error':
                return 'ExclamationCircleSolid';
            case 'announcement':
                return 'LightningBoltSolid';
        }
    }, [
        status
    ]);
    return jsx(FixedBannerProvider, {
        multiline: multiline,
        children: jsx(Headlessui.Transition, {
            ...rest,
            "aria-label": "banner",
            "aria-live": "assertive",
            className: fixedBannerClass,
            enter: "transition ease-out duration-100",
            enterFrom: classNames('transform opacity-0', {
                'scale-95': animationVariant === 'default'
            }),
            enterTo: classNames('transform opacity-100', {
                'scale-100': animationVariant === 'default'
            }),
            leave: "transition ease-in duration-75",
            leaveFrom: classNames('transform opacity-100', {
                'scale-100': animationVariant === 'default'
            }),
            leaveTo: classNames('transform opacity-0', {
                'scale-95': animationVariant === 'default'
            }),
            role: onDismiss ? 'alertdialog' : 'alert',
            show: visible,
            children: jsxs("div", {
                className: classNames('content-wrapper', {
                    multiline: multiline
                }),
                "data-testid": "fixed-banner-container-wrapper",
                children: [
                    !hideIcon && jsx(Icon, {
                        className: "banner-icon",
                        "data-testid": "icon",
                        name: fixedBannerIcon,
                        size: multiline ? 24 : 20
                    }),
                    jsx("div", {
                        className: "content",
                        "data-testid": "content",
                        children: children
                    }),
                    onDismiss && jsxs("button", {
                        className: "banner-dismiss",
                        "data-testid": "dismiss-icon",
                        onClick: onDismiss,
                        type: "button",
                        children: [
                            jsx("span", {
                                children: locale.dismiss
                            }),
                            jsx(Icon, {
                                "aria-hidden": "true",
                                focusable: "false",
                                name: "XSolid",
                                size: 20
                            })
                        ]
                    })
                ]
            })
        })
    });
};
FixedBanner.displayName = 'FixedBanner';
FixedBanner.Title = FixedBannerTitle;
FixedBanner.Description = FixedBannerDescription;
FixedBanner.Footer = FixedBannerFooter;

export { FixedBanner, FixedBanner as default };
//# sourceMappingURL=FixedBanner.es.js.map
